/**
 * AGENTBASESERVICE - GERENCIAMENTO DE AGENTES INSTALADOS
 *
 * Este serviço gerencia os AGENTES JÁ INSTALADOS nos produtos.
 * Responsável por:
 * - Instalar agentes do marketplace nos produtos
 * - Gerenciar agentes instalados (CRUD completo)
 * - Configurar settings e permissões de agentes
 * - Desinstalar agentes dos produtos
 * - Verificar versões e compatibilidade
 *
 * ENDPOINT: /agent-base
 * ESTADO: Agentes que ESTÃO INSTALADOS
 * INTERFACE: AgentBase (com status, is_public, created_by, etc.)
 *
 * DIFERENÇA: agentCatalogService (marketplace) vs agentBaseService (instalados)
 *
 * FLUXO: agentCatalogService (busca) → agentBaseService (instala/gerencia)
 */

import { getApi, postApi, delApi, get, post, put, del } from "@/shared/utils/fetchApi";
import {
  AgentBase,
  CreateAgentBase,
  UpdateAgentBase,
} from "../types/AgentBaseInterface";

/**
 * Classe de serviço para encapsular todas as operações relacionadas aos agent bases
 * Implementa o padrão Facade para simplificar as chamadas à API
 */
export class AgentBaseService {
  private static readonly BASE_URL = "/agent-base";

  /**
   * Obtém todos os agent bases
   * @returns Lista de agent bases
   */
  static async getAll(): Promise<AgentBase[]> {
    try {
      const data = await getApi<{ data: AgentBase[] }>(this.BASE_URL);
      return data.data;
    } catch (error) {
      console.error("[AgentBaseService] Erro ao buscar agent bases:", error);
      throw error;
    }
  }

  /**
   * Obtém agent base pelo ID
   * @param id ID do agent base
   * @returns Dados do agent base
   */
  static async getById(id: string): Promise<AgentBase> {
    try {
      return await get<AgentBase>(`/agent-bases/${id}`);
    } catch (error) {
      console.error(
        `[AgentBaseService] Erro ao buscar agent base com ID ${id}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Obtém agent bases públicos
   * @returns Lista de agent bases públicos
   */
  static async getPublic(): Promise<AgentBase[]> {
    try {
      const data = await getApi<{ data: AgentBase[] }>(`${this.BASE_URL}/public`);
      return data.data;
    } catch (error) {
      console.error(
        "[AgentBaseService] Erro ao buscar agent bases públicos:",
        error,
      );
      throw error;
    }
  }

  /**
   * Obtém agent bases por status
   * @param status Status do agent base
   * @returns Lista de agent bases
   */
  static async getByStatus(status: string): Promise<AgentBase[]> {
    try {
      const data = await getApi<{ data: AgentBase[] }>(
        `${this.BASE_URL}/status/${status}`,
      );
      return data.data;
    } catch (error) {
      console.error(
        `[AgentBaseService] Erro ao buscar agent bases com status ${status}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Cria um novo agent base
   * @param data Dados do agent base a ser criado
   * @returns Agent base criado
   */
  static async create(data: CreateAgentBase): Promise<AgentBase> {
    try {
      const result = await postApi<{ data: AgentBase }>(this.BASE_URL, data);
      return result.data;
    } catch (error) {
      console.error("[AgentBaseService] Erro ao criar agent base:", error);
      throw error;
    }
  }

  /**
   * Atualiza um agent base existente pelo ID
   * @param id ID do agent base
   * @param data Dados a serem atualizados
   * @returns Agent base atualizado
   */
  static async update(id: string, data: UpdateAgentBase): Promise<AgentBase> {
    try {
      return await put<AgentBase>(`/agent-bases/${id}`, data);
    } catch (error) {
      console.error(
        `[AgentBaseService] Erro ao atualizar agent base com ID ${id}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Remove um agent base existente pelo ID
   * @param id ID do agent base
   * @returns Mensagem de sucesso
   */
  static async delete(
    id: string,
  ): Promise<{ success: boolean; message: string }> {
    try {
      const result = await delApi<{ success: boolean; message: string }>(
        `${this.BASE_URL}/${id}`,
      );
      return result;
    } catch (error) {
      console.error(
        `[AgentBaseService] Erro ao excluir agent base com ID ${id}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Métodos compatíveis com a interface do Supabase para facilitar a migração
   */

  /**
   * Obtém um agent base pelo ID (formato compatível com Supabase)
   * @param id ID do agent base
   * @returns Dados no formato { data, error }
   */
  static async getAgentBase(
    id: string,
  ): Promise<{ data: AgentBase | null; error: Error | null }> {
    try {
      const res = await get<AgentBase>(`/agent-bases/${id}`);
      return { data: res, error: null };
    } catch (error: unknown) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { data: null, error: err };
    }
  }

  /**
   * Atualiza um agent base (formato compatível com Supabase)
   * @param id ID do agent base
   * @param data Dados a serem atualizados
   * @returns Status da operação no formato { error }
   */
  static async updateAgentBase(
    id: string,
    data: UpdateAgentBase,
  ): Promise<{ error: Error | null }> {
    try {
      await put(`/agent-bases/${id}`, data);
      return { error: null };
    } catch (error: unknown) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { error: err };
    }
  }

  /**
   * Cria um agent base (formato compatível com Supabase)
   * @param data Dados do agent base a ser criado
   * @returns Status da operação no formato { data, error }
   */
  static async createAgentBase(
    data: CreateAgentBase,
  ): Promise<{ data: AgentBase | null; error: Error | null }> {
    try {
      const result = await postApi<{ data: AgentBase }>(this.BASE_URL, data);
      return { data: result.data, error: null };
    } catch (error: unknown) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { data: null, error: err };
    }
  }

  /**
   * Remove um agent base (formato compatível com Supabase)
   * @param id ID do agent base
   * @returns Status da operação no formato { error }
   */
  static async deleteAgentBase(id: string): Promise<{ error: Error | null }> {
    try {
      await delApi(`${this.BASE_URL}/${id}`);
      return { error: null };
    } catch (error: unknown) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { error: err };
    }
  }

  /**
   * Obtém agent bases por product_id (formato compatível com Supabase)
   * @param workspaceId ID do produto
   * @returns Status da operação no formato { data, error }
   */
  static async getByProductId(
    workspaceId: string,
  ): Promise<{ data: AgentBase[] | null; error: Error | null }> {
    try {
      const list = await get<AgentBase[]>(
        `/agent-bases?product_id=${workspaceId}&include_available=true&include_integrations=true`,
      );

      if (list) {
        const filteredData = list
          .filter((agent: AgentBase) => agent.type === 0)
          .sort((a: AgentBase, b: AgentBase) => a.name.localeCompare(b.name));

        return { data: filteredData, error: null };
      }

      return { data: list, error: null };
    } catch (error: unknown) {
      console.error(
        `[AgentBaseService] Erro ao buscar agent bases por product_id ${workspaceId}:`,
        error,
      );
      const err = error instanceof Error ? error : new Error(String(error));
      return { data: null, error: err };
    }
  }

  /**
   * Obtém agent bases por product_id e tipo (formato compatível com Supabase)
   * @param workspaceId ID do produto
   * @param type Tipo do agente (0 = Workspace, 1 = Workflow, etc)
   * @returns Status da operação no formato { data, error }
   */
  static async getByProductIdAndType(
    workspaceId: string,
    type: number,
  ): Promise<{ data: AgentBase[] | null; error: Error | null }> {
    try {
      const result = await getApi<{ data: AgentBase[] }>(
        `${this.BASE_URL}/product/${workspaceId}/type/${type}`,
      );
      return { data: result.data, error: null };
    } catch (error: unknown) {
      const err = error instanceof Error ? error : new Error(String(error));
      return { data: null, error: err };
    }
  }

  /**
   * Obtém agentes do tipo Product por product_id (formato compatível com Supabase)
   * Substitui a consulta direta ao Supabase:
   * supabase.from('agent_base').select('*').eq('product_id', workspaceId).eq('type', 0).order('name')
   *
   * @param workspaceId ID do produto
   * @returns Status da operação no formato { data, error }
   */
  static async getAgentsByProductId(
    workspaceId: string,
  ): Promise<{ data: AgentBase[] | null; error: Error | null }> {
    try {
      // Usando o método getByProductId com filtro de tipo e ordenação
      const result = await this.getByProductId(workspaceId);

      return result;
    } catch (error: unknown) {
      console.error(
        `[AgentBaseService] Erro ao buscar agentes do tipo Product por product_id ${workspaceId}:`,
        error,
      );
      const err = error instanceof Error ? error : new Error(String(error));
      return { data: null, error: err };
    }
  }

  /**
   * Conta o número de agentes por product_id (formato compatível com Supabase)
   * Substitui a consulta direta ao Supabase:
   * supabase.from('agent_base').select('*', { count: 'exact', head: true }).eq('product_id', workspaceId)
   *
   * @param workspaceId ID do produto
   * @returns Status da operação no formato { count, error }
   */
  static async getCountByProductId(
    workspaceId: string,
  ): Promise<{ count: number | null; error: Error | null }> {
    try {
      const result = await getApi<{ count: number }>(
        `${this.BASE_URL}/product/${workspaceId}/count`,
      );
      return { count: result.count, error: null };
    } catch (error: unknown) {
      console.error(
        `[AgentBaseService] Erro ao contar agentes por product_id ${workspaceId}:`,
        error,
      );
      const err = error instanceof Error ? error : new Error(String(error));
      return { count: null, error: err };
    }
  }

  /**
   * Obtém as configurações (settings) de um agente pelo ID (formato compatível com Supabase)
   * Substitui a consulta direta ao Supabase:
   * supabase.from('agent_base').select('settings').eq('id', agentId).single()
   *
   * @param agentId ID do agente
   * @returns Status da operação no formato { data, error }
   */
  static async getSettingsById(
    agentId: string,
  ): Promise<{ data: Record<string, unknown> | null; error: Error | null }> {
    try {
      // agentId é agent_base id (instância), não template. Settings vivem em agent_base,
      // não em /agents/:id/settings (templates) — essa rota 404 (E_AGENT_TEMPLATE_NOT_FOUND).
      const ab = await get<AgentBase>(`/agent-bases/${agentId}`);
      return {
        data: (ab?.settings ?? {}) as Record<string, unknown>,
        error: null,
      };
    } catch (error: unknown) {
      console.error(
        `[AgentBaseService] Erro ao buscar settings do agente com ID ${agentId}:`,
        error,
      );
      const err = error instanceof Error ? error : new Error(String(error));
      return { data: null, error: err };
    }
  }

  /**
   * Verifica se já existe uma versão com o nome especificado para um agente
   * Substitui a consulta direta ao Supabase:
   * supabase.from('agent_versions').select('id').eq('agent_base_id', agentId).eq('version', versionName)
   *
   * @param agentId ID do agente base
   * @param versionName Nome da versão a ser verificada
   * @returns Status da operação no formato { data, error }
   */
  static async checkVersionNameExists(
    agentId: string,
    versionName: string,
  ): Promise<{ data: boolean; error: Error | null }> {
    try {
      const res = await get<{ available: boolean }>(
        `/agent-bases/${agentId}/versions/check/${versionName}`,
      );
      return { data: !res.available, error: null };
    } catch (error: unknown) {
      console.error("Error checking if version name exists:", error);
      const err = error instanceof Error ? error : new Error(String(error));
      return { data: false, error: err };
    }
  }

  /**
   * Instala um agente disponível em um produto específico
   *
   * `POST /agent-bases` deriva organização e usuário do token e copia os
   * settings do agente de origem no servidor. Por isso a assinatura não recebe
   * `organizationId`, `userId`, `settings`, `parameterValues` nem `promptValues`:
   * o ramo que rodava já os descartava com `void`, e um parâmetro aceito e
   * ignorado parece configuração aplicada.
   *
   * @param agentId ID do agente na tabela agents
   * @param workspaceId ID do produto onde o agente será instalado
   * @param filters Filtros de integrações e recursos selecionados durante a instalação
   * @returns Status da operação no formato { data, error }
   */
  static async installAgent(
    agentId: string,
    workspaceId: string,
    filters?: Array<{ name: string; type: string; value: string }>,
    selectedIntegrations?: Array<{
      integration_base_id: string;
      integration_name: string;
    }>,
  ): Promise<{
    data: Record<string, unknown> | null;
    error: { message: string; status?: number } | null;
  }> {
    try {
      const body = {
        agent_id: agentId,
        product_id: workspaceId,
        filters: filters || [],
        selected_integrations: selectedIntegrations || [],
      };
      const res = await post<Record<string, unknown>>("/agent-bases", body);

      return { data: res, error: null };
    } catch (error: unknown) {
      const err = error as Record<string, unknown>;
      const response = err.response as Record<string, unknown> | undefined;
      const responseData = response?.data as
        | Record<string, unknown>
        | undefined;
      // Extrair informações mais detalhadas do erro
      const errorMessage =
        (responseData?.message as string) ||
        (err.message as string) ||
        "Erro desconhecido";
      const errorStatus = response?.status as number | undefined;

      // Tratar diferentes tipos de erros
      let readableError = errorMessage;
      if (errorStatus === 409) {
        readableError = "Este agente já está instalado neste produto.";
      } else if (errorStatus === 404) {
        readableError = "Agente ou produto não encontrado.";
      }

      return {
        data: null,
        error: { message: readableError, status: errorStatus },
      };
    }
  }

  /**
   * Desinstala um agente de um produto
   * @param agentId ID do agente a ser desinstalado
   */
  static async uninstallAgent(agentId: string) {
    if (!agentId) {
      return {
        error: {
          message: "ID do agente não especificado",
        },
      };
    }

    try {
      const result = await del<{ success: boolean; message?: string }>(
        `/agent-bases/${agentId}`,
      );

      return {
        data: result || { success: true },
      };
    } catch (error: unknown) {
      console.error("[AgentBaseService] Erro ao desinstalar agente:", error);
      const err = error instanceof Error ? error : new Error(String(error));

      return {
        error: {
          message: err.message || "Erro ao desinstalar agente",
          status: (error as Record<string, unknown>).status as
            | number
            | undefined,
        },
      };
    }
  }
}
